import {
  selectIpxEstimatedRegionBytes,
  selectIpxOrderMode,
  selectIpxSentScanCount,
} from "@/store/modes/ipx-order-mode-selectors";
import { formatIpxDeliverables } from "@/store/modes/ipx-order-mode-thunks";
import { useAppSelector } from "@/store/store-hooks";
import {
  Alert,
  FaroButton,
  FaroText,
  grey,
  OpenLinkIcon,
} from "@faro-lotv/flat-ui";
import PrintIcon from "@mui/icons-material/Print";
import { Divider, Link, Stack } from "@mui/material";
import type { JSX, ReactNode } from "react";
import { IPX_ORDER_STATUS_URL } from "../ipx-config";
import { printIpxReceipt, ReceiptRow } from "../print-ipx-receipt";
import { formatDataSize, formatFloorArea } from "../ipx-measures-format";

/** @returns the receipt of the order placed and paid at IPX, and who to contact for what */
export function OrderPlacedPanel(): JSX.Element | null {
  const { order, regionMeasures } = useAppSelector(selectIpxOrderMode);
  const bytes = useAppSelector(selectIpxEstimatedRegionBytes);
  const scanCount = useAppSelector(selectIpxSentScanCount);
  if (!order) return null;

  const statusUrl = `${IPX_ORDER_STATUS_URL}?orderId=${encodeURIComponent(order.orderId)}`;
  const price = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: order.currency,
  }).format(order.price);

  // The same values on screen and in the printed receipt
  const rows: ReceiptRow[] = [
    { label: "IPX order", value: order.orderId },
    {
      label: "Deliverables",
      value: formatIpxDeliverables(order.deliverables),
    },
    ...(order.deliverables.includes("bimModel")
      ? [{ label: "Level of detail", value: order.levelOfDetail }]
      : []),
    {
      label: "Expected delivery",
      value: `~${order.deliveryDays} business ${order.deliveryDays === 1 ? "day" : "days"}`,
    },
    ...(scanCount === undefined
      ? []
      : [{ label: "Scans", value: scanCount.toLocaleString() }]),
    ...(bytes === undefined
      ? []
      : [
          {
            label: "Size",
            value: formatDataSize(bytes, regionMeasures?.export?.points),
          },
        ]),
    ...(regionMeasures
      ? [{ label: "Area", value: formatFloorArea(regionMeasures.planAreaM2) }]
      : []),
  ];
  const paidRow: ReceiptRow = { label: "Paid to IPX", value: price };

  return (
    <Stack sx={{ gap: 2 }}>
      <Alert
        variant="success"
        title="Order placed and paid at IPX"
        description="IPX sends you the receipt and the invoice by email."
      />
      <SummaryList>
        {rows.map(({ label, value }) => (
          <SummaryRow key={label} label={label}>
            {value}
          </SummaryRow>
        ))}
        <Divider />
        <SummaryRow label={paidRow.label}>{paidRow.value}</SummaryRow>
      </SummaryList>
      <Stack sx={{ gap: 0.5 }}>
        <FaroText variant="labelM">Who to contact</FaroText>
        <SummaryRow label="Order, payment and invoice">IPX</SummaryRow>
        <SummaryRow label="Sphere XG and the results">FARO support</SummaryRow>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* A link, as it leaves Sphere XG for the website of IPX */}
        <Link
          href={statusUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "0.75rem",
          }}
        >
          View order at IPX
          <OpenLinkIcon sx={{ width: 14, height: 14 }} aria-hidden />
        </Link>
        <FaroButton
          variant="ghost"
          size="s"
          icon={<PrintIcon aria-hidden />}
          onClick={() => printIpxReceipt([...rows, paidRow])}
        >
          Print receipt
        </FaroButton>
      </Stack>
    </Stack>
  );
}

/** @returns a framed list of labelled values */
export function SummaryList({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  return (
    <Stack
      component="dl"
      sx={{
        m: 0,
        p: 1.5,
        gap: 1,
        borderRadius: 1,
        backgroundColor: grey[50],
        border: `1px solid ${grey[200]}`,
      }}
    >
      {children}
    </Stack>
  );
}

type SummaryRowProps = {
  /** Name of the value */
  label: string;

  /** The value */
  children: ReactNode;
};

/** @returns a labelled value of a summary list */
export function SummaryRow({ label, children }: SummaryRowProps): JSX.Element {
  return (
    <Stack direction="row" sx={{ justifyContent: "space-between", gap: 2 }}>
      <FaroText component="dt" variant="bodyS" color={grey[600]}>
        {label}
      </FaroText>
      <FaroText
        component="dd"
        variant="labelM"
        sx={{ m: 0, textAlign: "right" }}
      >
        {children}
      </FaroText>
    </Stack>
  );
}
